"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { usePlayerStore, useSiteAuthStore } from "@gerama/stores";
import { isApiError, type Song } from "@gerama/types";
import { env } from "@/env";
import { browserCoreApi } from "@/lib/api";
import { openPlaylistPicker } from "@/features/playlists/playlist-picker";
import { openLogin } from "@/features/auth/login-modal";
import { songToTrack } from "@/features/player/to-track";
import { loadInteractionStatus } from "@/features/player/interaction-status";
import { songKeys } from "./query-keys";
import { publicPath } from "@/lib/public-path";
import { BRAND, appUri } from "@/lib/brand";
import { openInApp } from "@/lib/open-in-app";
import { invalidateLibrary } from "@/features/discover/library-keys";

export const savedKeys = {
  status: (itemType: string, id: number) => ["interaction", "saved", itemType, id] as const,
};

/** A short label for a credit role (a song's artists link with PRIMARY / ARTIST / FEATURED). */
export const ROLE_LABEL: Record<string, string> = {
  PRIMARY: "خواننده",
  ARTIST: "هنرمند",
  FEATURED: "با حضور",
};

export function songUrl(song: { id: number; code?: string | null }): string {
  return `${env.NEXT_PUBLIC_SITE_URL}${publicPath("songs", song)}`;
}

async function copy(text: string, done: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(done);
  } catch {
    toast.error("کپی نشد");
  }
}

/**
 * Everything the "…" menu, the song page and the player do to a song — one place,
 * so a row, the page and the full-screen player never drift apart. Actions that
 * need an account send a guest to sign in and back.
 */
export function useSongActions(song: Song, options: { loadDetail?: boolean } = {}) {
  const router = useRouter();
  const client = useQueryClient();
  const token = useSiteAuthStore((s) => s.token);

  const detail = useQuery({
    queryKey: songKeys.detail(song.code || song.id),
    queryFn: ({ signal }) => browserCoreApi.songs.detail(song.code || song.id, signal),
    enabled: !!options.loadDetail,
    staleTime: 5 * 60_000,
  });

  const saved = useQuery({
    queryKey: savedKeys.status("song", song.id),
    queryFn: () => loadInteractionStatus("song", song.id).then((s) => s.saved),
    enabled: !!token && !!options.loadDetail,
    staleTime: 60_000,
  });

  const requireAccount = React.useCallback(() => {
    if (token) return true;
    openLogin();
    return false;
  }, [token]);

  const toggleSaved = useMutation({
    mutationFn: () => browserCoreApi.playback.toggleSaved("song", song.id),
    onSuccess: (value) => {
      client.setQueryData(savedKeys.status("song", song.id), value);
      void invalidateLibrary(client);
      toast.success(value ? "ذخیره شد" : "از ذخیره‌شده‌ها حذف شد");
    },
    onError: () => toast.error("انجام نشد"),
  });

  const download = useMutation({
    mutationFn: () => browserCoreApi.songs.download(song.code || song.id),
    onSuccess: (result) => {
      if (!result?.url) return;
      const a = document.createElement("a");
      a.href = result.url;
      a.download = `${song.name}.m4a`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("دانلود شروع شد");
    },
    onError: (error) => {
      const blocked = isApiError(error) && /plan|download/i.test(`${error.code} ${error.message}`);
      if (blocked) {
        toast.error("اشتراک فعلی شما دانلود را پشتیبانی نمی‌کند", {
          action: { label: "اشتراک‌ها", onClick: () => router.push("/plans") },
        });
      } else toast.error("دانلود ناموفق بود");
    },
  });

  return {
    detail: detail.data,
    isSaved: !!saved.data,
    play: () => {
      usePlayerStore
        .getState()
        .playSingle(songToTrack(song), { label: song.name, href: publicPath("songs", song) });
    },
    addToQueue: () => {
      usePlayerStore.getState().addToQueue(songToTrack(song));
      toast.success("به صف اضافه شد");
    },
    addToPlaylist: () => {
      if (requireAccount())
        openPlaylistPicker({ itemType: "song", itemId: song.id, name: song.name });
    },
    toggleSaved: () => {
      if (requireAccount()) toggleSaved.mutate();
    },
    download: () => {
      if (requireAccount()) download.mutate();
    },
    copyLink: () => copy(songUrl(song), "لینک کپی شد"),
    copyUri: () => copy(appUri(`song/${song.code || song.id}`), `آدرس ${BRAND} کپی شد`),
    openInApp: () => openInApp(`song/${song.code || song.id}`),
  };
}
